import type { Role } from "../api/client";

/**
 * The audit's steps, and which of them a state has reached.
 *
 * **The specification numbers thirteen steps and the platform draws six stops.** §5 of the
 * implementation specification splits the work into three phases — pre-audit setup (1 to
 * 6), audit day (7 to 9) and reporting (10 to 13) — and the prototype's mastheads carry
 * those numbers verbatim: `Step 2 — scope and duration`, `Steps 4 and 5 — documents and
 * oversight`, `Audit day — steps 7 to 9`. Thirteen segments is not a rail anyone can read,
 * so the stops group them. The bar numbers its own six and the grouping is recorded on
 * `AUDIT_STEPS`, so a reader who follows a stop back to §5 finds the steps it covers — the
 * screens themselves still carry the spec's numbering in their mastheads.
 *
 * **Step 6 is deliberately not a stop.** Re-confirmation is not a position between the plan
 * and the documents — it is a condition that throws the audit *back* to step 2, which is
 * exactly what `PUT /audits/{id}/intake` does. Screen 48 draws it as a red callout above an
 * otherwise ordinary Step 2 screen and this follows that: `reconfirmation` returns the rail
 * to the determination and the callout says why. A permanent Step 6 segment would read as
 * something every audit passes through, and most never do.
 *
 * **This is not a wizard.** `StepRail` — the certification body's onboarding rail — tracks a
 * *furthest* index, because a wizard is one actor filling one form and the high-water mark
 * is a real fact about it. An audit has no such mark: the steps have different owners, the
 * work is done by three parties, and `audits.state` is the only thing that knows where it
 * has got to. So reachability is derived from the state and never from where the user has
 * been.
 */
export interface AuditStep {
  key: StepKey;
  title: string;
}

export type StepKey = "intake" | "determination" | "plan" | "readiness" | "session" | "report";

/**
 * The six stops, and the §5 steps each of them covers.
 *
 * The mapping is here in the comment because it is no longer on the bar: the segments
 * carried it in a mono eyebrow — `Step 1`, `Steps 4–5`, `Steps 10–13` — and the numbering
 * is now the stop's own position, one to six. A reader tracing a stop back to §5 is reading
 * this file; a reader looking at the bar wants to know where they are.
 *
 *   1 Intake             §5.1 step 1
 *   2 Scope and duration §5.1 step 2  (and step 6, which throws the audit back to it)
 *   3 Audit plan         §5.1 step 3
 *   4 Documents          §5.1 steps 4 and 5
 *   5 Audit day          §5.2 steps 7 to 9
 *   6 Report             §5.3 steps 10 to 13
 */
export const AUDIT_STEPS: readonly AuditStep[] = [
  { key: "intake", title: "Intake" },
  { key: "determination", title: "Scope and duration" },
  { key: "plan", title: "Audit plan" },
  { key: "readiness", title: "Documents" },
  { key: "session", title: "Audit day" },
  { key: "report", title: "Report" },
];

/**
 * Where the audit itself has got to — the stop its state sits at.
 *
 * `reconfirmation` resolves to the determination rather than to a stop of its own. That is
 * the whole of step 6: the intake moved, so the audit is back at step 2 with both approvals
 * withdrawn and the plan superseded, and the rail should say step 2 because that is the
 * work outstanding.
 *
 * **This is what opening an audit opens**, not only what the rail marks — so every answer
 * has to be a stop `reach` admits for both parties. `cancelled` is the one state where the
 * flow position is unknowable: it is terminal, no route writes it, and it says nothing
 * about how far the audit got. It resolves to the determination — the record of what the
 * audit was — because the alternative, falling through to the report, both claims Steps 1
 * to 5 were completed and names a report that was never drafted.
 *
 * **`awaiting_documents` needs a second fact, because the state stops moving there.** The
 * audit sits in it from the moment the plan is issued until the client opens the session, and
 * the planned date coming round writes nothing: `session_scheduled` is in the enum and
 * `portal.begin` never sets it — the audit goes straight to `session_live`, "because that is
 * what happened". So the state alone puts the audit at pre-audit readiness on the morning of
 * the audit, with the client already looking at *Begin audit session*, and the auditor whose
 * oversight begins the moment it opens landing two stops behind it.
 *
 * `auditDay` is `audits.audit_day` — the server's reading of the planned date, and the same
 * one the auditee's portal is served. It is not computed here: a browser clock in another
 * timezone deciding whether today is the audit day is the defect in a second copy.
 */
export function currentStep(state: string, auditDay = false): StepKey {
  switch (state) {
    case "draft":
    case "intake_complete":
      return "intake";
    case "determination_pending":
    case "determination_confirmed":
    case "reconfirmation":
    case "cancelled":
      return "determination";
    case "plan_generated":
      return "plan";
    case "plan_approved":
      return "readiness";
    case "awaiting_documents":
    case "session_scheduled":
      // Outstanding documents do not hold the position back. The client may open the session
      // on the day with slots unanswered — `POST /portal/session` asks for an approved plan
      // and the MD 4 gates, not for a full checklist — and what is missing is on Step 4–5,
      // where the count is. Waiting for the last upload to move the rail would leave the
      // auditor at readiness while the audit was being conducted.
      return auditDay ? "session" : "readiness";
    case "session_live":
    case "paused":
      return "session";
    default:
      return "report";
  }
}

/**
 * What a party may do at a stop, given the audit's state.
 *
 * Three answers rather than two, because "you cannot get there yet" and "you may look but
 * not touch" are different things to tell someone and a disabled segment says neither. The
 * reason travels with the verdict: a segment that refuses without saying why is the kind of
 * interface that gets worked around.
 */
export type Reach =
  | { reachable: false; why: string }
  | { reachable: true; writable: boolean; why?: string };

export function reach(step: StepKey, state: string, role: Role): Reach {
  switch (step) {
    case "intake":
      return intake(state, role);
    case "determination":
      return determination(state, role);
    case "plan":
      return plan(state, role);
    case "readiness":
      return readiness(state, role);
    case "session":
      return session(state, role);
    case "report":
      return report(state, role);
  }
}

/**
 * Step 1 — the certification body's record of its own client.
 *
 * **Editable long past both approvals**, and that is the built behaviour rather than a
 * relaxation of it: `PUT /audits/{id}/intake` refuses only the states below, and revising
 * before then runs the assertion 8 cascade — new determination persisted, both approvals
 * withdrawn, plan superseded, state `reconfirmation`. The specification's step 6 is that
 * cascade. Gating the screen at the first approval would make a tested regulatory path
 * unreachable; the cost is charged, not the change forbidden.
 *
 * The cut-off is the session, because an audit that has been *conducted* is corrected
 * through the report — it was conducted against the scope as it then stood.
 *
 * The lead auditor reads and does not write. The route says so (`require_roles(Role.CB)`)
 * and the reason is not access control for its own sake: the intake is one body's record of
 * its own client, and an auditor who disagrees with it has a lever already — the approval
 * they withhold at step 2.
 */
function intake(state: string, role: Role): Reach {
  if (role !== "cb") {
    return {
      reachable: true,
      writable: false,
      why: "The certification body maintains the intake. Your lever is the Step 2 approval.",
    };
  }
  if (CLOSED_TO_REVISION.has(state)) {
    return {
      reachable: true,
      writable: false,
      why:
        "This audit has been conducted. A Stage 1 audit is corrected through the report, " +
        "not by revising the intake it was planned from.",
    };
  }
  return { reachable: true, writable: true };
}

/** Mirrors `_CLOSED_TO_REVISION` in `api/routers/audits.py`. The server refuses regardless. */
const CLOSED_TO_REVISION = new Set([
  "session_live",
  "paused",
  "session_closed",
  "report_drafted",
  "report_under_review",
  "report_approved",
  "report_issued",
  "closed",
  "cancelled",
]);

/**
 * Step 2 — the determination, and the departures from it.
 *
 * **Two cards, two owners.** Cl. 8 factors and duration adjustments are `Role.CB`: the
 * lead auditor reads the ledger and approves it rather than editing the figures they are
 * approving, which is what makes the dual approval two decisions and not one.
 *
 * The **scope split** is both parties'. *This clause cannot be satisfied from a screen* is
 * a statement about audit technique, and the auditor who signs the finding owns audit
 * technique — so the server admits both roles to `POST /audits/{id}/scope-overrides` and
 * each reverts only its own. So the step is writable for both, and the `why` says which
 * half each of them writes rather than the rail marking it read-only and being wrong for
 * one card out of two.
 */
function determination(state: string, role: Role): Reach {
  if (state === "draft") {
    return { reachable: false, why: "No determination has been run for this audit yet." };
  }
  return role === "cb"
    ? { reachable: true, writable: true }
    : {
        reachable: true,
        writable: true,
        why:
          "You record scope overrides and give or withhold the auditor's approval. The " +
          "Cl. 8 factors and the duration adjustments are the certification body's.",
      };
}

/**
 * Step 3 — the plan, which is the lead auditor's professional judgement.
 *
 * No plan exists until both approvals are in: §5.1 step 2, and the platform means it
 * literally — there is nothing to open. The certification body reads it and does not amend
 * it, because a body editing the plan would make the words the auditor signs somebody
 * else's.
 */
function plan(state: string, role: Role): Reach {
  if (BEFORE_PLAN.has(state)) {
    return {
      reachable: false,
      why: "No plan exists until both Step 2 approvals are recorded.",
    };
  }
  return role === "lead_auditor"
    ? { reachable: true, writable: true }
    : {
        reachable: true,
        writable: false,
        why: "The plan is the lead auditor's professional judgement and carries their name.",
      };
}

const BEFORE_PLAN = new Set(["draft", "intake_complete", "determination_pending", "reconfirmation"]);

/** Steps 4 and 5 — the auditee uploads, the auditor reviews. Neither party edits here. */
function readiness(state: string, role: Role): Reach {
  void role;
  if (BEFORE_PLAN.has(state) || state === "determination_confirmed" || state === "plan_generated") {
    return {
      reachable: false,
      why: "The document checklist is issued with the approved plan.",
    };
  }
  return { reachable: true, writable: false };
}

/**
 * Steps 7 to 9 — the session.
 *
 * `awaiting_documents` is included because the auditor's oversight begins when the auditee
 * opens the session, and they need somewhere to be watching from before it does. The
 * certification body watches its own audit and sends nothing: the console arrives with
 * `can_intervene` false and the routes refuse it besides.
 */
function session(state: string, role: Role): Reach {
  if (!AUDIT_DAY.has(state) && !AFTER_SESSION.has(state)) {
    return { reachable: false, why: "The session opens on the audit day." };
  }
  return role === "lead_auditor"
    ? { reachable: true, writable: true }
    : { reachable: true, writable: false, why: "Observing. Every read is written to the trail." };
}

const AUDIT_DAY = new Set(["awaiting_documents", "session_scheduled", "session_live", "paused"]);

/**
 * Steps 10 to 13 — the report.
 *
 * Reachable from the moment the session closes and through every reporting state, because
 * withdrawing a signature returns a signed report to draft and the way back has to be a way
 * back to here. Both parties write, at different steps: the lead auditor drafts, amends and
 * signs (10 and 11); the certification body reviews and issues (12 and 13). Neither can do
 * the other's, so the rail cannot answer with one flag — it reports writable for both and
 * the screen projects the controls.
 */
function report(state: string, role: Role): Reach {
  void role;
  if (!AFTER_SESSION.has(state)) {
    return { reachable: false, why: "The report is drafted once the session closes." };
  }
  return { reachable: true, writable: true };
}

const AFTER_SESSION = new Set([
  "session_closed",
  "report_drafted",
  "report_under_review",
  "report_approved",
  "report_issued",
  "closed",
]);
